import { useTranslation } from 'react-i18next'
import { CurecallTooltip } from '@honestica/curecall-ui'
import { twMerge } from 'src/utils/tw-merge.util'
import { useTimezonePreference } from 'src/providers/timezone-preference.provider'
import {
  getFormattedDate,
  getFormattedDateWithHours,
  getInternationalSortableDateTimeString,
  getReadableDateWithHours,
} from 'src/utils/date-formatter.util'

type TFormatter = 'date' | 'dateWithHours' | 'readableDateWithHours' | 'sortableDateTime'

type Props = {
  date: Date | string | null | undefined
  formatter: TFormatter
  displaySeconds?: boolean
  variant?: 'light' | 'dark'
}

const formatDate = (
  t: any,
  date: Date | string | null | undefined,
  formatter: TFormatter,
  displaySeconds: boolean,
  timezone?: string,
): string => {
  if (!date) return ''
  switch (formatter) {
    case 'date':
      return getFormattedDate(date, timezone)
    case 'dateWithHours':
      return getFormattedDateWithHours(t, date, displaySeconds, timezone)
    case 'readableDateWithHours':
      return getReadableDateWithHours(t, date, timezone)
    case 'sortableDateTime':
      return getInternationalSortableDateTimeString(date, timezone)
  }
}

const TimezoneAwareTime = ({ date, formatter, displaySeconds = false, variant = 'light' }: Props) => {
  const { t } = useTranslation()
  const { displayTimezone, alternateTimezone, hasTimezoneMismatch, isProjectTimezoneSelected } =
    useTimezonePreference()

  const formattedDate = formatDate(t, date, formatter, displaySeconds, displayTimezone)

  if (!formattedDate) return null

  if (!hasTimezoneMismatch || !isProjectTimezoneSelected) {
    return <span>{formattedDate}</span>
  }

  const alternateFormattedDate = formatDate(t, date, formatter, displaySeconds, alternateTimezone)

  return (
    <CurecallTooltip
      tooltipPosition="bottom"
      content={t('components.timezone-aware-time.tooltip', {
        timezone: alternateTimezone,
        time: alternateFormattedDate,
      })}
    >
      <span
        className={twMerge(
          'c-bg-gradient-to-r c-bg-clip-text c-text-transparent c-text-xs',
          variant === 'dark' ? 'c-from-blue-300 c-to-secondary-300' : 'c-from-primary-500 c-to-secondary-700',
        )}
      >
        {formattedDate}
      </span>
    </CurecallTooltip>
  )
}

export { TimezoneAwareTime }
export default TimezoneAwareTime
